// GLSL for the holographic globe (docs/ART-DIRECTION.md §1). ShaderMaterial prefixes supply the matrices,
// `position`, `normal` and `cameraPosition`. Output stays in linear working space; OutputPass encodes.

/** Shared by the body and the atmosphere shell: view-space normal and view direction. */
export const FACING_VERTEX = /* glsl */ `
varying vec3 vNormal;
varying vec3 vView;

void main() {
  vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
  vNormal = normalize(normalMatrix * normal);
  vView = normalize(-mvPosition.xyz);
  gl_Position = projectionMatrix * mvPosition;
}
`

/** Near-black sphere whose centre falls into deep blue-violet. */
export const BODY_FRAGMENT = /* glsl */ `
uniform vec3 uLimbColor;
uniform vec3 uCoreColor;
uniform float uDepthPower;

varying vec3 vNormal;
varying vec3 vView;

void main() {
  float facing = clamp(dot(normalize(vNormal), normalize(vView)), 0.0, 1.0);
  gl_FragColor = vec4(mix(uLimbColor, uCoreColor, pow(facing, uDepthPower)), 1.0);
}
`

/** Thin fresnel rim on a slightly larger shell; fades out at the shell's own silhouette. Additive. */
export const ATMOSPHERE_FRAGMENT = /* glsl */ `
uniform vec3 uColor;
uniform float uIntensity;
uniform float uRimPower;
uniform float uOuterFade;

varying vec3 vNormal;
varying vec3 vView;

void main() {
  float facing = clamp(dot(normalize(vNormal), normalize(vView)), 0.0, 1.0);
  float rim = pow(1.0 - facing, uRimPower) * smoothstep(0.0, uOuterFade, facing);
  gl_FragColor = vec4(uColor, rim * uIntensity);
}
`

/** Land dot-matrix: world-sized round dots, faded towards the limb. Additive. */
export const LAND_DOTS_VERTEX = /* glsl */ `
uniform float uDotSize;
uniform float uPointScale;
uniform float uMinPointPx;

varying float vFacing;

void main() {
  vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
  vFacing = dot(normalize(normalMatrix * position), normalize(-mvPosition.xyz));
  gl_PointSize = max(uMinPointPx, uDotSize * uPointScale / -mvPosition.z);
  gl_Position = projectionMatrix * mvPosition;
}
`

export const LAND_DOTS_FRAGMENT = /* glsl */ `
uniform vec3 uColor;
uniform float uIntensity;
uniform float uLimbFade;

varying float vFacing;

void main() {
  vec2 fromCentre = gl_PointCoord - vec2(0.5);
  float radial = dot(fromCentre, fromCentre) * 4.0;
  if (radial > 1.0) discard;
  float alpha = (1.0 - radial) * smoothstep(0.0, uLimbFade, vFacing) * uIntensity;
  gl_FragColor = vec4(uColor, alpha);
}
`
